/* eslint-disable no-console */
/* eslint-disable max-len */
import { MutableRefObject, useCallback, useRef } from 'react';

type UseIntersectionObserverParams = {
  lastEleRef: MutableRefObject<HTMLElement | null>;
  loaderRef: MutableRefObject<boolean>;
  count: number;
  data: any[];
  setPage: (updateFn: (prevPage: number) => number) => void;
};

const useIntersectionObserver = ({
	lastEleRef,
	loaderRef,
	count,
	data,
	setPage
}: UseIntersectionObserverParams) =>
{
	const observerRef = useRef<IntersectionObserver | null>(null);

	const LoadMoreList = useCallback(() =>
	{
		// Clean up existing observer first
		if (observerRef.current && lastEleRef?.current)
		{
			observerRef.current.unobserve(lastEleRef.current);
			observerRef.current.disconnect();
			observerRef.current = null;
		}

		const currentElement = lastEleRef?.current;
		const currentCount = count;
		const currentDataLength = data?.length || 0;

		if (currentElement)
		{
			const observer = new IntersectionObserver(
				(entries) =>
				{
					if (entries[0]?.isIntersecting)
					{
						// Use the current values from closure, not stale ones
						const shouldLoadMore = !loaderRef?.current && currentCount > currentDataLength;

						if (shouldLoadMore)
						{
							loaderRef.current = true;
							setPage((prev) => prev + 1);
						}
					}
				},
				{
					root       : null,
					rootMargin : '0px',
					threshold  : 0.5
				}
			);

			observerRef.current = observer;
			observer.observe(currentElement);

			return () =>
			{
				if (observerRef.current && currentElement)
				{
					observerRef.current.unobserve(currentElement);
					observerRef.current.disconnect();
					observerRef.current = null;
				}
			};
		}

		return () => {};
	}, [ count, data?.length, setPage ]);

	return LoadMoreList;
};

export default useIntersectionObserver;
